/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useMemo} from 'react';

import {useAccessToken} from '@salesforce/commerce-sdk-react';
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {configHelper} from '@amblique/base/shared/helpers/config-helper';
import {usePreview} from '@amblique/king-living/app/hooks/use-preview';

/**
 * @override of `@amblique/base/app/hooks/use-service`
 *
 * Full replacement (not a wrapper): the parent computes the memoized `variables`
 * object internally and immediately instantiates the service, leaving no seam to
 * inject the Contentful-preview flag. So we replicate the parent verbatim and add
 * preview-awareness. Keep this in sync if the base `useService` changes.
 *
 * Service factory
 * Retrieves necessary variables for service instantiation and creates the service instance
 *
 * @template T
 *
 * @param {new() => T} Service
 *
 * @returns {T}
 */
export function useService(Service) {
  const names = Service.variables.sort();
  let app, commerceAPI, locale, origin, shopAPI, site, token;

  if (names.includes('app')) {
    (app = configHelper.getConfig('app'));
  }

  if (names.includes('commerceAPI')) {
    (commerceAPI = configHelper.getConfig('app.commerceAPI.parameters'));
  }

  if (names.includes('locale') || names.includes('site')) {
    ({site, locale} = useMultiSite());
  }

  if (names.includes('origin')) {
    origin = getAppOrigin();
  }

  if (names.includes('shopAPI')) {
    (shopAPI = configHelper.getConfig('app.shopAPI'));
  }

  if (names.includes('token')) {
    ({token} = useAccessToken());
  }

  // Contentful content preview: services opt in with `static previewAware = true`.
  // `preview` is folded into the instance variables (NOT into `Service.variables`)
  // so it isolates the cache/instance hash for preview vs published WITHOUT
  // gating instantiation — a `false` flag must never block the service from
  // mounting. Hook is called unconditionally to keep hook order stable.
  // See docs/contentful/preview/README.md §5.3-5.4.
  const previewAware = Service.previewAware === true;
  const preview = usePreview();

  const variables = useMemo(() => {
    const values = {app, commerceAPI, locale, origin, shopAPI, site, token};

    if (previewAware) {
      values.preview = preview;
    }

    return Object.keys(values)
      .reduce((variables, name) => {
        if (values[name] !== undefined) {
          variables[name] = values[name];
        }

        return variables;
      }, {})
    ;
  }, [names.join('|'), app, commerceAPI, locale, origin, shopAPI, site, token, previewAware, preview]);

  return useMemo(() => {
    return Service.variables.every((variable) => !!variables[variable]) && Service.instantiate(variables);
  }, [variables]);
}
